import axios from 'axios'
import { StatusCodes } from 'http-status-codes'

import { L_STORAGE_KEYS, PATHS } from '@src/consts'
import { useSignout } from '@src/hooks/queries'
import {
  BASE_CREDIT_API_URL,
  BASE_IAM_URL,
  BASE_SOLARIS_URL,
  BASE_THEMIS_URL,
  BASE_URL,
  assertNever,
  lStorage,
} from '@src/utils/'

import { UseAxios } from './type'

let isRetry = true

export const useAxios = ({
  apiType = 'USER_API',
  shouldSkipLogout = false,
}: UseAxios = {}) => {
  const { mutate } = useSignout()

  const axiosInstance = axios.create({
    baseURL: getApiType(),
  })

  function getApiType() {
    switch (apiType) {
      case 'USER_API':
        return BASE_URL()
      case 'IAM':
        return BASE_IAM_URL()
      case 'THEMIS':
        return BASE_THEMIS_URL()
      case 'CREDIT_API':
        return BASE_CREDIT_API_URL()
      case 'SOLARIS':
        return BASE_SOLARIS_URL()
      default:
        assertNever(apiType)
        break
    }
  }

  function isSignInPage(path: string) {
    return path === PATHS.SIGNIN
  }

  axiosInstance.interceptors.request.use((config) => {
    const token = lStorage.getItem(L_STORAGE_KEYS.TOKEN)
    // To be able to request the Staging API from Local to increase development efficiency
    if (token && process.env.NODE_ENV !== 'production') {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Since nuxt and next have different port numbers,
    // they are not considered the same origin, so withCredentials must be enabled.
    // To access to internal client api not one for OAuth2.
    // External APIs use JWT token auth instead of session cookies, so they don't require
    // withCredentials and upsidr-private-client-api-internal header
    const isExternalApi = apiType === 'CREDIT_API' || apiType === 'SOLARIS'

    if (!isExternalApi) {
      config.withCredentials = true
      config.headers['upsidr-private-client-api-internal'] = 'true'
    }

    return config
  })

  axiosInstance.interceptors.response.use(undefined, async (err) => {
    const error = err.response

    // NOTE: In the use-login-two-factor-auth-app.ts, to bypass the process in the event of a 401 error, I have set shouldSkipLogout to true.
    if (
      error.status === StatusCodes.UNAUTHORIZED &&
      isRetry &&
      !isSignInPage(window.location.pathname) &&
      !shouldSkipLogout
    ) {
      const backUrl =
        window.location.pathname + window.location.search + window.location.hash
      mutate({ backUrl, isSessionTimeout: true })
      isRetry = false
      return
    }

    return Promise.reject(error)
  })

  return { axiosInstance }
}
